import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import styled from '@emotion/styled'
import { useTheme } from '@mui/system'

import { Button, ButtonVariant } from 'src/system/ui/button/Button'

const DISCORD_URL = 'https://pureskill.gg/discord'

const KNOWN_ERROR_CODES = new Set([
  'demo_not_found',
  'demo_error',
  'replay_error',
  'ppp_error',
  'sharecode_error',
  'metademo_error',
  'demo_too_large'
])

const DISCORD_ERROR_CODES = new Set([
  'replay_error',
  'ppp_error',
  'demo_too_large',
  'unknown'
])

export function ErrorCodeModal(props: {
  errorCode: string
  isVisible: boolean
  onClose: () => void
  matchId?: string | null
}) {
  const { t } = useTranslation()
  const theme = useTheme()

  const code = KNOWN_ERROR_CODES.has(props.errorCode)
    ? props.errorCode
    : 'unknown'
  const prefix = `matches:error_modal.${code}`
  const title = t(`${prefix}.title`)
  const body = t(`${prefix}.body`)
  const buttonLabel = t(`${prefix}.button`)
  const opensDiscord = DISCORD_ERROR_CODES.has(code)

  function handleButton() {
    if (opensDiscord) {
      window.open(DISCORD_URL, '_blank', 'noopener,noreferrer')
    }
    props.onClose()
  }

  return (
    <Dialog open={props.isVisible} onClose={props.onClose}>
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <DialogContentText>{body}</DialogContentText>
        {props.matchId && <MatchIdField matchId={props.matchId} />}
      </DialogContent>
      <DialogActions>
        <StyledButton
          onClick={handleButton}
          variant={ButtonVariant.primary}
          color={opensDiscord ? theme.d.colors.discord.blurple : undefined}
        >
          {buttonLabel}
        </StyledButton>
      </DialogActions>
    </Dialog>
  )
}

function MatchIdField(props: { matchId: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    try {
      const promise = navigator.clipboard?.writeText(props.matchId)
      if (!promise) {
        setCopied(false)
        return
      }

      promise.then(
        () => setCopied(true),
        () => setCopied(false)
      )
    } catch {
      setCopied(false)
    }
  }

  return (
    <MatchIdBox>
      <MatchIdLabel>{t('matches:error_modal.match_id_label')}</MatchIdLabel>
      <MatchIdRow>
        <MatchIdCode>{props.matchId}</MatchIdCode>
        <CopyButton type='button' onClick={handleCopy}>
          {copied
            ? t('matches:error_modal.copied')
            : t('matches:error_modal.copy')}
        </CopyButton>
      </MatchIdRow>
    </MatchIdBox>
  )
}

const StyledButton = styled(Button)`
  text-transform: uppercase;
`

const MatchIdBox = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[4]};
`

const MatchIdLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: ${(props) => props.theme.d.spacing[1]};
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const MatchIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.d.spacing[3]};
`

const MatchIdCode = styled.code`
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
  color: ${(props) => props.theme.d.colors.text.primary};
`

const CopyButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  text-transform: uppercase;
  color: ${(props) => props.theme.d.colors.discord.blurple};
`
